/** View-only column for separating whole-record AI proposals from committed rows.
 *
 * The client-side grid currently owns sort/filter execution, so materializing a small display value
 * on each row lets the ordinary AG column filter, header sort, multi-sort panel, filter builder, and
 * saved columnState all use the same key. Keep this in a leaf module so the projection is testable
 * without mounting AG Grid. When Lists moves to SSRM, this key must be mirrored by getRows.
 */
import type { CrmGridColumn } from '../../types';

/**
 * Is this row a whole-record AI PROPOSAL? A proposed row carries `_proposed_record` — `{source,
 * source_id}`, or `{}` pre-binding, both truthy.
 *
 * **Lives in this leaf module so the grid AND the record panel share ONE predicate.** It drives the
 * grid's full-row-green tint, the Accept/Dismiss row-menu gate, and (§15 item 9) the panel's
 * whole-record proposal tone — three surfaces that must agree about whether a record is a proposal.
 * The panel cannot import it from `CrmListGrid` without pulling AG Grid into a module that never
 * mounts it, so the predicate moved down here rather than being written a second time.
 */
export function isProposedRecordRow(row: Record<string, unknown> | null | undefined): boolean {
  return !!(row && row._proposed_record);
}

export const AI_PROPOSED_ROW_FIELD = '__ai_proposed_row__';
export const AI_PROPOSED_ROW_VALUE = 'AI proposed';
export const CURRENT_ROW_VALUE = 'Current';

export const AI_PROPOSED_ROW_COLUMN: CrmGridColumn = {
  field_key: AI_PROPOSED_ROW_FIELD,
  label: 'AI proposed row',
  field_type: 'enum',
  comparator: null,
  origin: 'view',
};

export function withAiProposedRowColumn(
  columns: CrmGridColumn[], enabled: boolean,
): CrmGridColumn[] {
  if (!enabled || columns.some((c) => c.field_key === AI_PROPOSED_ROW_FIELD)) return columns;
  return [...columns, AI_PROPOSED_ROW_COLUMN];
}

/**
 * FALLBACK ONLY, for one release (spec §12.4).
 *
 * The value is server-owned as of W2: `get_rows` / `get_record` project `__ai_proposed_row__` onto
 * the row itself, with the raw values kept BYTE-IDENTICAL to the two constants above so saved
 * set-filters and the two prod views referencing this key survive untouched. This synthesis now
 * fills the key only when the backend did not send it — an older server, or a list on which the
 * system field is not enabled — so old FE + new BE and new FE + old BE both work during the
 * rollback window. It must not overwrite a value the server sent: the server's is the governed one
 * (it is the value sort, filter and export agree on), and clobbering it would reintroduce the
 * client-computed state W2 exists to remove.
 *
 * Delete this function, and `enabled`'s plumbing, once telemetry shows no response without the key.
 */
export function withAiProposedRowValues(
  rows: Array<Record<string, unknown>>, enabled: boolean,
): Array<Record<string, unknown>> {
  if (!enabled) return rows;
  return rows.map((row) => (
    row[AI_PROPOSED_ROW_FIELD] !== undefined ? row : {
      ...row,
      [AI_PROPOSED_ROW_FIELD]: row._proposed_record ? AI_PROPOSED_ROW_VALUE : CURRENT_ROW_VALUE,
    }
  ));
}
